<template>
  <iColumn gap="small" width="fill" wrap="nowrap">
    <iSelect
      :model-value="selectedMicrophoneDeviceId"
      :items="microphoneItems"
      label="Microphone"
      width="fill"
      return-value
      :empty-string="describeEmptyState(availableAudioInputDevices, 'audioinput')"
      :disabled="!microphoneItems.length"
      data-test="settings-microphone-select"
      @update:model-value="$emit('change-microphone', $event)"
    />
    <iSelect
      v-if="canChooseOutput"
      :model-value="selectedOutputDeviceId"
      :items="speakerItems"
      label="Speaker"
      width="fill"
      return-value
      :empty-string="describeEmptyState(availableOutputDevices, 'audiooutput')"
      :disabled="!speakerItems.length"
      data-test="settings-speaker-select"
      @update:model-value="$emit('change-output', $event)"
    />

    <iColumn
      gap="extraSmall"
      width="fill"
      wrap="nowrap"
      :style-overrides="{paddingTop: 'small'}"
    >
      <iSelect
        :model-value="noiseCancellationMode"
        :items="noiseCancellationItems"
        label="Noise cancellation"
        width="fill"
        return-value
        :disabled="!audioProcessing.supported"
        data-test="settings-noise-cancellation-select"
        @update:model-value="$emit('change-noise-cancellation', $event)"
      />
      <iText font-size="small" variant="subtle" data-test="noise-cancellation-description">
        {{ modeDescription }}
      </iText>
      <iText font-size="small" :variant="statusVariant" data-test="noise-cancellation-status">
        {{ noiseCancellationStatus }}
      </iText>
      <iText
        v-if="echoCancellationLost"
        font-size="small"
        variant="error"
        data-test="echo-cancellation-lost"
      >
        Echo cancellation is off because another tab or app is using this microphone. Close it, or wear headphones.
      </iText>
    </iColumn>

    <iColumn
      gap="small"
      width="fill"
      wrap="nowrap"
      :style-overrides="{paddingTop: 'small'}"
    >
      <iText font-weight="dark" font-size="small">
        Hear how you sound
      </iText>
      <iText font-size="small" variant="subtle" data-test="audio-mic-test-status">
        {{ micTestStatus }}
      </iText>
      <iRow gap="small" width="fill" wrap="nowrap">
        <iButton
          variant="secondary"
          size="small"
          :disabled="micTest.recording || micTest.playing"
          :is-loading="micTest.recording"
          data-test="audio-mic-test"
          @click="$emit('record-mic-test')"
        >
          Record 5 seconds
        </iButton>
        <iButton
          variant="tertiary"
          size="small"
          :disabled="!micTest.durationSeconds || micTest.recording || micTest.playing"
          data-test="audio-mic-test-play"
          @click="$emit('play-mic-test')"
        >
          Play back
        </iButton>
      </iRow>
    </iColumn>
  </iColumn>
</template>

<script>
import { describeDevices, describeEmptyState, supportsOutputSelection } from "%/apps/video-engine/device-catalog";

const MODE_ITEMS = [
  { id: "standard", name: "Standard" },
  { id: "strong", name: "Strong" },
  { id: "browser", name: "Browser only" },
];

const MODE_DESCRIPTIONS = {
  standard: "Removes background noise, typing and clicks, and keeps your voice natural.",
  strong: "Cuts everything between your words harder. Use it somewhere noisy.",
  browser: "Uses your browser's basic noise suppression. More typing and background noise gets through.",
};

const FAILED_TO_START_REASONS = ["voice-processor-failed", "voice-runtime-error"];

export default {
  name: "AudioSettingsSection",
  props: {
    availableAudioInputDevices: {
      type: Array,
      default: () => [],
    },
    availableOutputDevices: {
      type: Array,
      default: () => [],
    },
    selectedMicrophoneDeviceId: {
      type: String,
      default: null,
    },
    selectedOutputDeviceId: {
      type: String,
      default: null,
    },
    noiseCancellationMode: {
      type: String,
      default: "standard",
    },
    audioProcessing: {
      type: Object,
      default: () => ({ supported: true, phase: "idle", captureMismatches: [] }),
    },
    micTest: {
      type: Object,
      default: () => ({ recording: false, playing: false, durationSeconds: 0 }),
    },
  },
  emits: [
    "change-microphone",
    "change-output",
    "change-noise-cancellation",
    "record-mic-test",
    "play-mic-test",
  ],
  computed: {
    noiseCancellationItems() {
      return MODE_ITEMS;
    },
    modeDescription() {
      return MODE_DESCRIPTIONS[this.noiseCancellationMode] || MODE_DESCRIPTIONS.standard;
    },
    noiseCancellationStatus() {
      const processing = this.audioProcessing || {};
      if (!processing.supported) {
        return "This browser can't run our noise cancellation, so its own noise suppression is on.";
      }
      if (processing.phase === "fallback") {
        return FAILED_TO_START_REASONS.includes(processing.fallbackReason)
          ? "Noise cancellation couldn't start on this device, so your browser's own noise suppression is on instead."
          : "This computer couldn't keep up, so your browser's own noise suppression is on instead.";
      }
      if (this.noiseCancellationMode === "browser") {
        return "Browser noise suppression is on.";
      }
      if (processing.phase === "loading") {
        return "Starting noise cancellation…";
      }
      if (processing.phase === "ready") {
        return this.noiseCancellationMode === "strong" ? "Strong noise cancellation is on." : "Noise cancellation is on.";
      }

      return "Noise cancellation turns on when you join.";
    },
    statusVariant() {
      return this.audioProcessing?.phase === "fallback" ? "error" : "subtle";
    },
    echoCancellationLost() {
      const mismatches = this.audioProcessing?.captureMismatches || [];

      return mismatches.some(mismatch => mismatch.setting === "echoCancellation" && mismatch.wanted === true);
    },
    micTestStatus() {
      if (this.micTest.recording) {
        return "Recording your microphone with noise cancellation applied…";
      }
      if (this.micTest.playing) {
        return "Playing back. Your microphone is muted while it plays.";
      }
      if (this.micTest.durationSeconds) {
        return `Recorded ${Math.round(this.micTest.durationSeconds)} seconds. Play it back to hear yourself as others do.`;
      }

      return "Record a few seconds and hear yourself the way others do.";
    },
    microphoneItems() {
      return describeDevices(this.availableAudioInputDevices, "audioinput");
    },
    speakerItems() {
      return describeDevices(this.availableOutputDevices, "audiooutput");
    },
    canChooseOutput() {
      return supportsOutputSelection();
    },
  },
  methods: {
    describeEmptyState,
  },
};
</script>
